// src/pages/dashboard/operators_admin.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Button,
  TextField,
  Divider,
  TableRow,
  TableCell,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SortableTable, { HeadCell } from "../../components/SortableTable";
import { useUserProvider } from "../../providers/useUserProvider";
import { useNavigate } from "react-router-dom";

import {
  getAllUsers,
  getAllShapes,
} from "../../api/stats";

type Period = "30" | "60" | "90" | "total" | "custom";

type OperatorRow = {
  id_utente: number;
  nome_completo: string;
  email: string;
  shapes_nel_periodo: number;
};

const cardSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
};

function normalizeDate(d?: string): Date | null {
  if (!d) return null;
  const date = new Date(d);
  return isNaN(date.getTime()) ? null : date;
}

function isInRange(d: Date, start?: Date | null, end?: Date | null): boolean {
  if (!start && !end) return true;
  const t = d.getTime();
  if (start && t < start.getTime()) return false;
  if (end && t > end.getTime()) return false;
  return true;
}

const OperatorsAdmin: React.FC = () => {
  const { user } = useUserProvider();
  const isAdmin = user.userType === 1;
  const navigate = useNavigate();

  const [period, setPeriod] = useState<Period>("total");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [ops, setOps] = useState<OperatorRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Calcolo date in base al periodo
  const rangeDates = useMemo(() => {
    if (period === "custom" && fromDate && toDate) {
      return {
        start: normalizeDate(fromDate),
        end: normalizeDate(toDate),
      };
    }
    if (period === "30" || period === "60" || period === "90") {
      const days = Number(period);
      const now = new Date();
      const start = new Date();
      start.setHours(0,0,0,0);
      start.setDate(now.getDate() - (days - 1));
      return { start, end: now };
    }
    return { start: null, end: null }; // total
  }, [period, fromDate, toDate]);

  // Carico operatori + shapes per calcolare i volumi per operatore
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        setLoading(true);

        const users = await getAllUsers(true, null); // tutti
        const operatori = users
          .filter((u) => u.id_tipo_utente === 3)
          .map((u) => ({
            id_utente: u.id_utente,
            nome_completo: `${u.nome ?? ""} ${u.cognome ?? ""}`.trim() || `Operatore #${u.id_utente}`,
            email: u.email ?? "",
            shapes_nel_periodo: 0,
          }));

        const allShapes = await getAllShapes(true, null);
        // Filtra shapes per intervallo
        const filtered = allShapes.filter((s) => {
          const d = normalizeDate(s.timestamp_creazione || s.created_at || s.data_creazione);
          return d && isInRange(d, rangeDates.start, rangeDates.end);
        });

        // Conta shapes per id_creatore
        const counts: Record<number, number> = {};
        for (const sh of filtered) {
          const idc = Number(sh.id_creatore || 0);
          if (!idc) continue;
          counts[idc] = (counts[idc] ?? 0) + 1;
        }

        const rows = operatori.map((op) => ({
          ...op,
          shapes_nel_periodo: counts[op.id_utente] ?? 0,
        }));

        setOps(rows);
      } catch (err) {
        console.error("Errore caricamento operatori:", err);
        setOps([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin, rangeDates]);

  const headCells: HeadCell<OperatorRow>[] = [
    { id: "nome_completo", label: "Operatore" },
    { id: "email", label: "Email" },
    { id: "shapes_nel_periodo", label: "Shape nel periodo", numeric: true },
    { id: "azioni" as any, label: "Azioni", disableSort: true },
  ];

  if (!isAdmin) {
    return (
      <Box p={3}>
        <Typography color="error">Accesso non autorizzato.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "calc(100vh - 64px)" }}>
      {/* Titolo + sottotitolo */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800 }}>
          Monitoraggio Operatori
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}>
          Elenco degli operatori e volume di shape nel periodo selezionato. Filtra per intervallo o range personalizzato.
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 2 }}>
        {/* Box tabella */}
        <Card sx={{ ...cardSx }}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PeopleAltOutlinedIcon color="action" />
              <Typography variant="subtitle1">Operatori</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {ops.length === 0 ? "Nessun operatore trovato" : `${ops.length} operatori`}
            </Typography>
          </Box>
          <Divider />
          <CardContent>
            <SortableTable<OperatorRow>
              rows={ops}
              headCells={headCells}
              renderRow={(row) => (
                <TableRow key={row.id_utente}>
                  <TableCell>{row.nome_completo}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell align="right">{row.shapes_nel_periodo}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityOutlinedIcon />}
                      sx={{ textTransform: "none", borderRadius: 999 }}
                      onClick={() => navigate(`/admin/operator/${row.id_utente}`, {
                        state: { nome_completo: row.nome_completo, email: row.email }
                      })}
                    >
                      Dettagli
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              rowsPerPageOptions={[10, 25, 50]}
              initialRowsPerPage={25}
            />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default OperatorsAdmin;
